// src/components/issues/SlackConfigDialog.jsx - Connect the user's own Slack workspace (OAuth) and pick a channel
import React, { useState, useEffect } from "react";
import ConnectionAccount from "../brain/ConnectionAccount";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { CheckCircle2, AlertCircle, Loader2, MessageSquare } from "lucide-react";
import {
  connectIntegration,
  getIntegrationStatus,
  getIntegrationSettings,
  saveIntegrationSettings,
  listSlackChannels,
} from "../../lib/integrations";

export default function SlackConfigDialog({ open, onClose, onSave }) {
  const [status, setStatus] = useState(null); // { team } when connected
  const [channels, setChannels] = useState([]);
  const [config, setConfig] = useState({ channel: "", lookbackHours: 24 });
  const [busy, setBusy] = useState(null); // "connect" | "save" | null
  const [result, setResult] = useState(null);

  const refresh = async () => {
    const [connection, prefs] = await Promise.all([getIntegrationStatus(), getIntegrationSettings()]);
    setStatus(connection.slack);
    setConfig({ ...prefs.slack, channel: '' });
    setChannels([]);
    if (connection.slack) {
      const list = await listSlackChannels();
      setChannels(list);
      if (list.some(channel => channel.id === prefs.slack.channel)) {
        setConfig((p) => ({ ...p, channel: prefs.slack.channel }));
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    refresh().catch((error) => setResult({ success: false, message: error.message }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy("connect");
    setResult(null);
    try {
      await connectIntegration("slack");
      await refresh();
      setResult({ success: true, message: "Slack connected. Pick a channel, then Save & Import." });
    } catch (error) {
      setResult({ success: false, message: error.message });
    } finally {
      setBusy(null);
    }
  };

  const saveAndImport = async () => {
    if (!config.channel) {
      setResult({ success: false, message: "Choose a channel first." });
      return;
    }
    const hours = Number(config.lookbackHours);
    if (!Number.isFinite(hours) || hours < 1 || hours > 720) {
      setResult({ success: false, message: "Use a lookback period from 1 to 720 hours." });
      return;
    }
    setBusy("save");
    setResult(null);
    try {
      const prefs = { channel: config.channel.trim(), lookbackHours: hours, realtimeSync: !!config.realtimeSync };
      await saveIntegrationSettings("slack", prefs);
      const importCount = await onSave?.(prefs);
      setResult({ success: true, message: `✅ Saved! Imported ${importCount || 0} Slack alerts.` });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      setResult({ success: false, message: `Import failed: ${error.message}` });
      if (error.reconnect) setStatus(null);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 500 }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
            <MessageSquare size={20} color="#4a154b" />
            Connect to Slack
          </DialogTitle>
          <DialogDescription>
            Sign in with your own Slack account to import alerts from your workspace.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "grid", gap: 16 }}>
          {result && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 12,
              borderRadius: 6,
              background: result.success ? "var(--success-soft)" : "var(--danger-soft)",
              border: `1px solid ${result.success ? "var(--success-border)" : "var(--danger-border)"}`
            }}>
              {result.success ? <CheckCircle2 size={16} color="var(--success)" /> : <AlertCircle size={16} color="var(--danger)" />}
              <span style={{ fontSize: 13, color: result.success ? "var(--success-fg)" : "var(--danger-fg)" }}>{result.message}</span>
            </div>
          )}

          {!status ? (
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 16, textAlign: "center" }}>
              <Button
                onClick={connect}
                disabled={busy === "connect"}
                style={{ background: "#4a154b", color: "white", border: "none", padding: "12px 24px" }}
              >
                {busy === "connect" ? (
                  <><Loader2 size={16} className="animate-spin" style={{ marginRight: 8 }} />Waiting for Slack…</>
                ) : (
                  <><MessageSquare size={16} style={{ marginRight: 8 }} />Connect with Slack</>
                )}
              </Button>
              <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 8 }}>
                A popup opens where you approve access to your Slack workspace.
              </div>
            </div>
          ) : (
            <>
              <ConnectionAccount provider="slack" connection={status} disabled={!!busy} onChange={refresh}/>
              <div style={{ fontSize: 14 }}>
                Workspace: <strong>{status.team?.name}</strong>
              </div>

              <div>
                <Label htmlFor="channel">Slack Channel</Label>
                {channels.length > 0 ? (
                  <select
                    id="channel"
                    value={config.channel}
                    onChange={(e) => setConfig((p) => ({ ...p, channel: e.target.value }))}
                    style={{ width: "100%", padding: 8, border: "1px solid var(--border)", borderRadius: 4, fontSize: 13 }}
                  >
                    <option value="">Select a channel...</option>
                    {channels.map((ch) => (
                      <option key={ch.id} value={ch.id}>#{ch.name} {ch.is_private ? "🔒" : ""}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id="channel"
                    placeholder="C1234567890"
                    value={config.channel}
                    onChange={(e) => setConfig((p) => ({ ...p, channel: e.target.value }))}
                  />
                )}
                <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
                  Messages from this channel are imported as issues.
                </div>
              </div>

              <div>
                <Label htmlFor="lookbackHours">Lookback Period (Hours)</Label>
                <Input
                  id="lookbackHours"
                  type="number"
                  min="1"
                  max="720"
                  value={config.lookbackHours}
                  onChange={(e) => setConfig((p) => ({ ...p, lookbackHours: parseInt(e.target.value) || 24 }))}
                />
                <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 4 }}>
                  How far back to look for messages (1-720 hours)
                </div>
              </div>

              <label style={{ display: "flex", gap: 8 }}><input type="checkbox" checked={!!config.realtimeSync} onChange={e => setConfig(current => ({ ...current, realtimeSync: e.target.checked }))}/>Keep this channel updated in Firefighter while I am a member</label>
              <Button
                onClick={saveAndImport}
                disabled={busy === "save"}
                style={{ background: "var(--success)", color: "white", border: "none" }}
              >
                {busy === "save" ? <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Importing…</> : "Save & Import"}
              </Button>
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
